import React from 'react'
import { Chip, Tooltip, Typography } from '@material-ui/core'
import AutorenewIcon from '@material-ui/icons/Autorenew'
import { useV3Styles, scheduleMeta, priorityMeta, queueStatusMeta } from './v3Styles'

const QueueCard = ({ item, selected, onClick }) => {
  const classes = useV3Styles()
  const status = scheduleMeta[item.status] || scheduleMeta.ok
  const showPriorityBadge = item.priorityOverride === 'rush' || item.priorityOverride === 'high'
  const showQueueChip = item.queueStatus && item.queueStatus !== 'draft'
  const areaSummary = item.areaSummary || { areaCount: 0, totalAcres: 0 }
  const sprayProgress = item.sprayProgress || {}
  const isMaint = item.hasHerbicideContract
  const needsAspireCompletion = item.needsAspireCompletion
  const activity = item.fieldActivitySummary || {}
  const hasActivity = Number(activity.totalCount || 0) > 0

  return (
    <div
      className={`${classes.queueCard} ${selected ? classes.queueCardSelected : ''}`}
      style={{
        borderLeftColor: selected ? undefined : status.borderColor,
        backgroundColor: selected ? undefined : (isMaint ? 'transparent' : '#fdf6f0')
      }}
      onClick={() => onClick(item._id)}
    >
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Typography className={classes.queueCardName} style={selected ? { color: '#1565c0' } : {}}>
          {item.siteName || '(unnamed)'}
        </Typography>
        <div style={{ display: 'flex', gap: 4 }}>
          {showPriorityBadge && (
            <Chip
              size="small"
              label={priorityMeta[item.priorityOverride].label}
              style={{
                backgroundColor: priorityMeta[item.priorityOverride].color,
                color: '#fff',
                height: 18,
                fontSize: 9,
                fontWeight: 700
              }}
            />
          )}
          {showQueueChip && (
            <Chip
              size="small"
              label={queueStatusMeta[item.queueStatus]?.label || item.queueStatus}
              style={{
                backgroundColor: queueStatusMeta[item.queueStatus]?.color || '#78909c',
                color: queueStatusMeta[item.queueStatus]?.textColor || '#fff',
                height: 18,
                fontSize: 9
              }}
            />
          )}
          {needsAspireCompletion && (
            <Tooltip title="Aspire has hours on this herbicide ticket, but the ticket is not complete.">
              <Chip
                size="small"
                label="Ticket"
                style={{
                  backgroundColor: '#f3e5f5',
                  color: '#7b1fa2',
                  height: 18,
                  fontSize: 9,
                  fontWeight: 700
                }}
              />
            </Tooltip>
          )}
        </div>
      </div>
      <Typography className={classes.queueCardAddress}>{item.address || '—'}</Typography>
      <div className={classes.queueCardMeta}>
        <span style={{ color: status.color, fontSize: 10, fontWeight: 600 }}>
          {status.label}
          {item.daysUntilNext != null && (
            <> &middot; {item.daysUntilNext < 0 ? `${Math.abs(item.daysUntilNext)}d past` : `${item.daysUntilNext}d`}</>
          )}
        </span>
        <span style={{ marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: 4 }}>
          {item.blueDyePreference === 'yes' && (
            <Tooltip title="Blue dye requested">
              <span style={{ color: '#1976d2', fontSize: 11 }}>💧</span>
            </Tooltip>
          )}
          {isMaint && (
            <Tooltip title="Recurring herbicide work">
              <span style={{
                display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                width: 20, height: 20, borderRadius: '50%',
                backgroundColor: '#e8f5e9',
                color: '#2e7d32'
              }}>
                <AutorenewIcon style={{ fontSize: 13 }} />
              </span>
            </Tooltip>
          )}
        </span>
      </div>
      <div className={classes.queueCardAreas}>
        {areaSummary.areaCount > 0 ? (
          <>
            <span style={{ fontSize: 10, color: selected ? '#1565c0' : '#888' }}>{areaSummary.areaCount} areas</span>
            <span style={{ fontSize: 10, color: '#aaa' }}>&middot;</span>
            <span style={{ fontSize: 10, color: selected ? '#1565c0' : '#888' }}>{areaSummary.totalAcres} acres</span>
            {sprayProgress.operationCount > 0 && (
              <>
                <span style={{ fontSize: 10, color: '#aaa' }}>&middot;</span>
                <span style={{ fontSize: 10, color: sprayProgress.status === 'completed' ? '#2e7d32' : '#f57f17', fontWeight: 600 }}>
                  {sprayProgress.completedAreaCount || 0}/{sprayProgress.plannedAreaCount || areaSummary.areaCount} done
                </span>
              </>
            )}
          </>
        ) : (
          <span style={{ fontSize: 10, color: '#e65100', fontStyle: 'italic' }}>No areas mapped</span>
        )}
      </div>
      {hasActivity && (
        <div className={classes.queueCardActivity}>
          {activity.issueCount > 0 && (
            <span className={classes.queueActivityBadge} style={{ color: '#c62828', background: '#ffebee' }}>
              {activity.issueCount} issue{activity.issueCount === 1 ? '' : 's'}
            </span>
          )}
          {activity.photoCount > 0 && (
            <span className={classes.queueActivityBadge} style={{ color: '#1565c0', background: '#e3f2fd' }}>
              {activity.photoCount} photo{activity.photoCount === 1 ? '' : 's'}
            </span>
          )}
          {activity.messageCount > 0 && (
            <span className={classes.queueActivityBadge} style={{ color: '#5d4037', background: '#efebe9' }}>
              {activity.messageCount} msg
            </span>
          )}
          {activity.completionCount > 0 && (
            <span className={classes.queueActivityBadge} style={{ color: '#2e7d32', background: '#e8f5e9' }}>
              Complete
            </span>
          )}
        </div>
      )}
    </div>
  )
}

export default QueueCard
